import { useEffect } from 'react';
import * as Sentry from '@sentry/react';
import { ORPCError } from '@orpc/client';
import {
  USER_SESSION_HEARTBEAT_INTERVAL_MS,
  USER_SESSION_INPUT_IDLE_MS,
} from '@erp/rpc';
import { rpcClient } from '@/api/rpc/client';
import { isImitationActive } from '@/api/imitationState';
import { isNetworkError } from '@/integrations/sentry/is-network-error';

/** Cap client-side Sentry noise when offline / backend is down. */
const SENTRY_REPORT_COOLDOWN_MS = 5 * 60_000;
let lastSentryReportAt = 0;

/**
 * Signals that a person is actually at the keyboard. `pointermove` and
 * `scroll` fire often, but the handler only stores a timestamp. `scroll` is
 * listened to in the capture phase because it does not bubble from the
 * scrolling element.
 */
const INPUT_EVENTS = [
  'pointerdown',
  'pointermove',
  'keydown',
  'wheel',
  'scroll',
  'touchstart',
] as const;

function isExpectedHeartbeatFailure(error: unknown): boolean {
  // Logged-out / no-org beats are normal; don't page on them.
  if (
    error instanceof ORPCError &&
    (error.code === 'UNAUTHORIZED' || error.code === 'FORBIDDEN')
  ) {
    return true;
  }
  // Offline, tab navigating away mid-beat, or a transient backend blip surface
  // as a fetch NetworkError. Expected for a best-effort background beat — don't
  // report them.
  const message = error instanceof Error ? error.message : '';
  return isNetworkError(message);
}

/**
 * Sends a periodic activity heartbeat to the backend while the app tab is
 * visible AND the person has interacted with it recently, so we can measure
 * how long people spend in Gently. A visible tab alone is not presence (a
 * second monitor, a locked screen), so beats stop USER_SESSION_INPUT_IDLE_MS
 * after the last input and resume on the next one. Best-effort: failures are
 * swallowed and never surface to the user.
 */
export function useSessionHeartbeat() {
  useEffect(() => {
    let inFlight = false;
    // Loading the app is itself an interaction.
    let lastInputAt = Date.now();
    // True once a tick was skipped for inactivity, so the next input beats
    // immediately instead of waiting up to a full interval.
    let idle = false;

    async function sendHeartbeat() {
      if (document.visibilityState !== 'visible' || inFlight) {
        return;
      }
      if (Date.now() - lastInputAt > USER_SESSION_INPUT_IDLE_MS) {
        idle = true;
        return;
      }
      idle = false;
      // The beat is a POST, which the imitation guard refuses — and an admin
      // looking at someone else's screen isn't that user spending time in
      // Gently, so there'd be nothing worth recording even if it were allowed.
      // Checked per beat rather than per mount so it follows the session state.
      if (isImitationActive()) {
        return;
      }
      inFlight = true;
      try {
        await rpcClient.userSessions.heartbeat();
      } catch (error) {
        // Tracking must never break the app — drop the beat and try again
        // on the next interval. Unexpected failures go to Sentry (throttled).
        const now = Date.now();
        if (
          !isExpectedHeartbeatFailure(error) &&
          now - lastSentryReportAt >= SENTRY_REPORT_COOLDOWN_MS
        ) {
          lastSentryReportAt = now;
          Sentry.captureException(error, {
            tags: { feature: 'user-session-heartbeat' },
            fingerprint: ['user-session-heartbeat'],
          });
        }
      } finally {
        inFlight = false;
      }
    }

    function handleInput() {
      const now = Date.now();
      // `idle` is only set by a tick, so also catch input that lands after the
      // idle window but before the next tick has noticed.
      const wasIdle = idle || now - lastInputAt > USER_SESSION_INPUT_IDLE_MS;
      lastInputAt = now;
      if (wasIdle) {
        void sendHeartbeat();
      }
    }

    function handleVisibilityChange() {
      // Fires on both hide and show; sendHeartbeat no-ops when hidden, so
      // this only records the moment the user returns to the tab — which is
      // itself an interaction.
      if (document.visibilityState === 'visible') {
        lastInputAt = Date.now();
      }
      void sendHeartbeat();
    }

    void sendHeartbeat();
    const intervalId = window.setInterval(
      () => void sendHeartbeat(),
      USER_SESSION_HEARTBEAT_INTERVAL_MS,
    );
    document.addEventListener('visibilitychange', handleVisibilityChange);
    for (const type of INPUT_EVENTS) {
      document.addEventListener(type, handleInput, {
        capture: true,
        passive: true,
      });
    }

    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      for (const type of INPUT_EVENTS) {
        document.removeEventListener(type, handleInput, { capture: true });
      }
    };
  }, []);
}
